import { ChangeDetectorRef, ContentChild, Directive, EventEmitter, inject, Input, Output, TemplateRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, type ValidationErrors } from '@angular/forms';
import { MbsLabelSize, MbsSize, MbsValidators } from '../../utils';
import { InputAppendDirective } from './input-append.directive';
import { ValidClasses } from './input-base.model';
import { ValidationErrorType } from './input-base.types';
import { InputErrorsDirective } from './input-errors.directive';
import { InputLabelDirective } from './input-label.directive';
import { MbsNgClass } from './input-label/input-label.model';
import { InputButton } from './input-pre-append/input-button';
import { InputPrependDirective } from './input-prepend.directive';

@Directive()
export abstract class InputBase<T> implements ControlValueAccessor {
  protected myValue: T = null;

  @Input()
  public set value(value: T) {
    this.myValue = value;
    this.onChange?.(value);
    this.cd.markForCheck();
    this.change.emit(value);
  }

  public get value(): T {
    return this.myValue;
  }

  /**
   * HTML Id
   */
  @Input() public id: string = 'input-' + Math.random().toString(36).substring(7);
  /**
   * Input placeholder
   */
  @Input() public placeholder = '';
  /**
   * Input autofocus
   */
  @Input() public autofocus = false;
  /**
   * Input label.
   * if `string` then use default template
   * `TemplateRef` will insert
   */
  @Input() public label: string | TemplateRef<any>;

  @Input() public labelPosition: 'left' | 'right' | 'top' | 'bottom';

  /**
   * CSS Classes for label content wrapper
   */
  @Input() public labelContentClasses: MbsNgClass;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Input() public name: string;
  /**
   * HTML tabindex
   */
  @Input() public tabindex = 0;

  /**
   * Disable input with ignore FormControl warnings
   */
  @Input() public disabledSelf = false;
  /**
   * Disable input
   */
  @Input() public disabled: boolean;
  /**
   * Determines whether the element is disabled (takes into disabled and disabledSelf)
   */
  get disabledState(): boolean {
    return (this.disabled != null && this.disabled !== false) || (this.disabledSelf != null && this.disabledSelf !== false);
  }

  /**
   * Readonly input
   */
  @Input() public readonly: boolean;
  /**
   * Returns readonly status (true if explicitly specified and not false)
   */
  public get readonlyState(): boolean {
    return this.readonly != null && this.readonly !== false;
  }

  /**
   * Classes for label
   */
  @Input() labelClasses: MbsNgClass;

  /**
   * @ignore
   */
  public get errors(): ValidationErrorType[] {
    if (!this.ngControl.errors || this.ngControl.pristine || this.ngControl.control?.valid) {
      return [];
    }

    return this.mapErrors(this.ngControl.errors);
  }

  /**
   * Label info. Append info icon
   */
  @Input() public info: string;

  /**
   * Label tooltip position
   */
  @Input() public infoPlacement = 'auto';

  /**
   * Specifies events that should trigger the tooltip
   */
  @Input() public infoTriggers = 'hover focus';

  /**
   * A selector specifying the element the tooltip should be appended to.
   */
  @Input() public infoContainer;

  @Input() public autocomplete: 'on' | 'off' | 'new-password' | string = 'on';
  /**
   * Flag for label `text-overflow` CSS-class
   */
  @Input() public textOverflow = false;

  /**
   * Radio size `lg` or `sm`.
   */
  @Input() public size: MbsSize.xxs | MbsSize.xs | MbsSize.sm | MbsSize.md | MbsSize.lg = null;

  /**
   * Label size from `xxs` to `lg`.
   */
  @Input() public labelSize: MbsLabelSize;

  /**
   * Generates a CSS class for the form control's size.
   * For example, for size='sm', returns 'form-control-sm'.
   * @return {string} The CSS class of the size, or an empty string if no size is specified
   */
  public get sizeClass(): string {
    return this.size ? 'form-control-' + this.size : '';
  }

  /** Displaying '*' for required fields is the default behavior, but can be overridden. */
  @Input() public showRequiredMark = true;

  /**
   * if [isErrorsShow]="false" we hidden mbs-input-errors
   */
  @Input() isErrorsShow = true;

  /**
   * Returns an object of CSS classes that reflect the current validation state of the control.
   * The classes correspond to Angular forms: ng-valid, ng-invalid, ng-touched, etc.
   * @return {ValidClasses} An object with validation state classes
   */
  public get validClasses(): ValidClasses {
    const control = this.ngControl?.control;
    const hasValidators = !!(control?.validator || control?.asyncValidator);

    return {
      'ng-untouched': !!control?.untouched,
      'ng-touched': !!control?.touched,
      'ng-pristine': !!control?.pristine,
      'ng-dirty': !!control?.dirty,
      'ng-valid': !!control?.valid,
      'ng-invalid': hasValidators && !!control?.invalid,
      'ng-pending': !!control?.pending
    };
  }

  @Input() prependButtons: InputButton[];
  @Input() appendButtons: InputButton[];

  @ContentChild(InputPrependDirective, { read: TemplateRef, static: true }) prepend: TemplateRef<any>;
  @ContentChild(InputAppendDirective, { read: TemplateRef, static: true }) append: TemplateRef<any>;

  @ContentChild(InputLabelDirective, { static: true }) labelTemplate: InputLabelDirective;
  @ContentChild(InputErrorsDirective, { static: true }) errorsTemplate: InputErrorsDirective;

  protected ngControl: NgControl;

  /**
   * Required indicator
   * Append red wildcard to label
   */
  public get hasRequiredValidator(): boolean {
    const control = this.ngControl?.control;

    if (control?.validator) {
      const validators = control.validator(new FormControl());

      return Boolean(validators?.required);
    }

    return false;
  }

  @Output() change = new EventEmitter<T>();
  @Output() blur = new EventEmitter<Event>();
  @Output() focus = new EventEmitter<FocusEvent>();

  protected cd = inject(ChangeDetectorRef);

  protected constructor() {
    const ngControl = inject(NgControl, { optional: true, self: true });
    this.ngControl = ngControl || ({} as any);

    if (ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  onChange: (value: T) => void;
  onTouched: () => void;

  writeValue(obj: T): void {
    this.myValue = obj;
    this.cd.markForCheck();
  }
  registerOnChange(fn: (value) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }

  handleBlur(event: Event): void {
    if (this['trim'] && this.value?.['trim']) {
      this.myValue = (this.value as any).trim();
      this.ngControl?.control?.setValue(this.myValue);
    }

    // https://angular.io/api/forms/AbstractControl A control is marked touched once the user has triggered a blur event on it.
    if (this.onTouched) {
      this.onTouched();
    }

    this.blur.emit(event);
  }

  handleFocus(event: FocusEvent): void {
    if (this.onTouched) {
      this.onTouched();
    }

    this.focus.emit(event);
  }

  public mapErrors(errors: ValidationErrors): ValidationErrorType[] {
    if (!errors) return [];

    return Object.entries(errors)
      .map(([key, value]) => {
        const error: ValidationErrorType = { key, ...value };

        if (!error.message) {
          const getMessage = MbsValidators.validatorMessages[key] as ((err: any) => string) | undefined;
          error.message = getMessage?.(error);
        }

        return error;
      })
      .filter((error) => !!error.message);
  }

  get buttonsSize(): MbsSize.sm | MbsSize.lg {
    switch (this.size) {
      case MbsSize.lg:
      case MbsSize.md:
        return MbsSize.lg;
      case MbsSize.sm:
        return MbsSize.sm;
      default:
        return null;
    }
  }
}
